import logo from "@/assets/logo.png";
import { ArrowLeft, ShieldCheck, DeviceMobile, CheckCircle, XCircle } from "@phosphor-icons/react";

const PAGE_VERSION = "1.0";
const LAST_UPDATED = "June 13, 2026";

const MESSAGE_TYPES = [
  "Published work schedules", "Shift changes", "PTO request updates",
  "Task assignments", "Meeting reminders", "Account security notices",
  "Company announcements", "Important operational alerts",
];

const STEPS = [
  "The employee logs into Short Stack Crew.",
  "The employee opens Settings.",
  "The employee selects Notifications.",
  "The employee selects SMS Notifications.",
  "The employee enters or confirms their mobile phone number.",
  "The employee sees the complete SMS disclosure.",
  "The employee voluntarily selects “Enable SMS Notifications”.",
  "The employee may instead select “Not Now” and continue using the app without SMS.",
  "The system records the consent only after the employee actively confirms “I Agree - Enable SMS Notifications”.",
];

const DISCLOSURE =
  "By selecting Enable SMS Notifications, you agree to receive work-related text messages from Short Stack Hawaii LLC at the mobile number you provide. Messages may include schedule notifications, shift changes, PTO updates, task assignments, meeting reminders, account security notices, company announcements, and operational alerts. Message frequency varies based on employee activity. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance. Consent is optional and is not a condition of employment or use of the Short Stack Crew application.";

function H({ children }) {
  return <h2 className="font-display text-xl font-bold mb-2 mt-8">{children}</h2>;
}

export default function SmsConsent() {
  return (
    <div className="min-h-screen bg-background" data-testid="sms-consent-page">
      <header className="sticky top-0 z-10 bg-card/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <img src={logo} alt="Short Stack Hawai'i" className="h-9 w-auto" />
          <a href="/" data-testid="sms-consent-back" className="press inline-flex items-center gap-1.5 text-sm font-bold px-4 h-9 rounded-full border-2 border-black bg-white">
            <ArrowLeft size={15} weight="bold" /> Back to app
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10 text-sm text-black/70 leading-relaxed">
        <h1 className="font-display text-3xl font-black tracking-tight mb-1">Short Stack Hawaii LLC SMS Notifications</h1>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-black/40 mb-6">Version {PAGE_VERSION} · Last updated {LAST_UPDATED}</p>

        <p>Short Stack Hawaii LLC offers optional work-related SMS notifications to authorized employees through the Short Stack Crew application. Messages may include published work schedules, shift changes, PTO request updates, task assignments, meeting reminders, account security notices, company announcements, and important operational alerts.</p>
        <p className="mt-3">Employees voluntarily enable SMS notifications from their Short Stack Crew notification settings. SMS consent is optional and is not required to create an account, use the application, receive work assignments, or remain employed.</p>

        <H>SMS Disclosure</H>
        <div data-testid="sms-consent-disclosure" className="rounded-xl border-2 border-black bg-white p-4 text-black">
          <p>{DISCLOSURE}</p>
        </div>

        <div className="flex flex-wrap gap-3 mt-4">
          <a href="/privacy" data-testid="sms-consent-privacy-btn" className="press inline-flex items-center gap-1.5 text-sm font-bold px-4 h-10 rounded-full border-2 border-black bg-white"><ShieldCheck size={16} weight="bold" /> View Privacy Policy</a>
          <a href="/terms" data-testid="sms-consent-terms-btn" className="press inline-flex items-center gap-1.5 text-sm font-bold px-4 h-10 rounded-full border-2 border-black bg-white">View Terms and Conditions</a>
        </div>

        <H>Messages you may receive</H>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 list-disc pl-5">
          {MESSAGE_TYPES.map((m) => <li key={m}>{m}</li>)}
        </ul>

        <H>How Employees Opt In</H>
        <p>Authorized employees opt in after logging into Short Stack Crew and navigating to <strong>Settings → Notifications → SMS Notifications</strong>. The employee enters or confirms a mobile phone number, reviews the SMS disclosure, and actively selects <strong>“I Agree - Enable SMS Notifications.”</strong> The employee may select <strong>“Not Now”</strong> and continue using the application without SMS. Consent is optional and is not required as a condition of employment, account creation, or access to the application.</p>

        <H>The complete opt-in process</H>
        <ol className="space-y-2 list-decimal pl-5" data-testid="sms-consent-steps">
          {STEPS.map((s, i) => <li key={i}>{s}</li>)}
        </ol>

        <div className="mt-6 grid sm:grid-cols-[minmax(0,300px)_1fr] gap-5 items-start">
          <figure className="m-0">
            <img src="/sms-opt-in.png" alt="Screenshot of the Short Stack Crew opt-in screen under Settings → Notifications → SMS Notifications, showing the mobile number field, the enable control, and the SMS consent disclosure."
              className="w-full max-w-[300px] rounded-xl border-2 border-black" data-testid="sms-consent-screenshot" />
            <figcaption className="text-xs text-black/40 mt-2">Actual opt-in screen inside the app (Settings → Notifications → SMS Notifications).</figcaption>
          </figure>
          <div className="space-y-3">
            <div className="flex items-start gap-2"><DeviceMobile size={18} weight="bold" className="mt-0.5 shrink-0" /><p className="m-0">The employee enters or confirms their mobile number and reviews the full disclosure above.</p></div>
            <div className="flex items-start gap-2"><CheckCircle size={18} weight="bold" className="mt-0.5 shrink-0 text-green-700" /><p className="m-0"><strong>Enable SMS Notifications</strong> is never preselected. The employee must actively confirm <strong>“I Agree - Enable SMS Notifications.”</strong></p></div>
            <div className="flex items-start gap-2"><XCircle size={18} weight="bold" className="mt-0.5 shrink-0 text-black/40" /><p className="m-0">The employee may choose <strong>Not Now</strong> and continue using all app features without SMS. They can opt out anytime by disabling SMS in settings or replying STOP.</p></div>
          </div>
        </div>

        <H>Opting out</H>
        <p>You can cancel at any time by replying <strong>STOP</strong> to any message, or by disabling SMS notifications in your settings. Reply <strong>HELP</strong> for assistance. Message frequency varies. Message and data rates may apply.</p>

        <H>Privacy</H>
        <p>Short Stack Hawaii LLC does not sell, rent, or share mobile phone numbers or SMS consent information with third parties or affiliates for marketing or promotional purposes. Mobile information may only be shared with service providers as necessary to deliver requested messaging services and comply with legal obligations.</p>

        <div className="mt-8 pt-5 border-t border-border text-sm">
          <p className="font-bold text-black">Short Stack Hawaii LLC</p>
          <p>Email: <a className="underline" href="mailto:shortstackllc@gmail.com">shortstackllc@gmail.com</a><br />
            Website: <a className="underline" href="https://www.shortstackhawaiicrew.com">https://www.shortstackhawaiicrew.com</a></p>
          <p className="mt-3 text-black/40 text-xs">This page demonstrates the opt-in process and does not enroll public visitors into SMS messaging. Only authorized employees can opt in from within their Short Stack Crew account.</p>
        </div>
      </main>
    </div>
  );
}
